/*
English resource file. Mirror of fr.ts — same nested key shape.
First-pass translations; review for tone consistency before
shipping to a real English-speaking deployment.
*/

export const en =
{
	common:
	{
		loading:			"Loading…",
		error_network:		"Network error.",
		error_loading:		"Loading error: {{message}}",
		never:				"never",
		none:				"none",
		dash:				"—",
		yes:				"Yes",
		no:					"No",
		cancel:				"Cancel",
		confirm:			"Confirm",
		save:				"Save",
		update:				"Update",
		updating:			"Updating…",
		send:				"Send",
		sending:			"Sending…",
		clear:				"Clear",
		prev:				"← Previous",
		next:				"Next →",
		page_of:			"Page {{page}} of {{total}}",
		rows_per_page:		"Rows per page",
		page_size_label:	"Rows per page",
		download:			"Download",
		downloading:		"Downloading…",
		retry:				"Retry",
		error_state_title:	"Something went wrong",
		from:				"From",
		to:					"To",
	},

	nav:
	{
		app_name:			"SpirApps",
		my_cards:			"My cards",
		shared_with_me:		"Shared with me",
		my_account:			"My account",
		statistics:			"Statistics",
		users:				"Users",
		audit:				"Audit",
		logout:				"Sign out",
		copyright:			"© Spirtech 2026",
	},

	auth:
	{
		login_title:					"SpirApps",
		login_subtitle_passwordless:	"Enter your email address; a 6-digit sign-in code will be sent to you.",
		email_label:					"Email",
		code_label:						"6-digit code",
		send_code_submit:				"Send code",
		login_submit:					"Sign in",
		login_progress:					"Signing in…",
		code_sent_info:					"If an account exists for that email, a code has just been sent. Check your inbox (and spam folder). The code is valid for 1 hour.",
		code_resent_info:				"A new code has just been sent. The previous code is no longer valid.",
		code_step_subtitle:				"Enter the code sent to {{email}}.",
		change_email_link:				"Use a different email",
		resend_code_link:				"Resend code",

		err_invalid_credentials:	"Wrong or expired code.",
		err_account_disabled:		"Account disabled. Contact the administrator.",
		err_missing_field:			"Please fill in all required fields.",
		err_rate_limited:			"Too many attempts. Try again in a few minutes.",
		err_default:				"Sign-in error. Please try again.",
		session_expired_banner:		"Session expired. Please sign in again.",
	},

	cards:
	{
		title:					"My cards",
		title_count:			"My cards ({{total}})",
		no_cards:				"No cards uploaded yet.",
		no_cards_with_filters:	"No cards match the filters.",
		col_serial:				"Serial number",
		col_type:				"Type",
		col_model:				"Model",
		col_folder:				"Folder",
		col_read_on:			"Read on",
		filter_serial:			"Serial number…",
		filter_type:			"Type (e.g. CALYPSO_PRIME)",
		filter_type_all:		"All types",
		filter_model:			"Model (e.g. INTERCODE_2_2)",
		filter_model_all:		"All models",
		filter_folder:			"Folder (prefix)",
		filter_date_min:		"Min read date",
		filter_date_max:		"Max read date",
		delete_tooltip:			"Delete",
		delete_aria:			"Delete card {{serial}}",
		delete_dialog_title:	"Delete card?",
		delete_dialog_body:		"This action is irreversible.",
		delete_dialog_button:	"Delete",
		export_csv:				"Export CSV",
		export_xlsx:			"Export Excel",
	},

	compare:
	{
		title:				"Two-card comparison",
		section_title:		"Compare this card",
		button:				"Compare with…",
		picker_title:		"Pick the card to compare with",
		picker_search:		"Serial number…",
		picker_empty:		"No other card available to compare.",
		picker_cancel:		"Cancel",
		card_type:			"Type",
		data_model:			"Model",
		read_date:			"Read on",
		folder:				"Folder",
		section_select:		"SELECT",
		section_sv:			"SV",
		section_environment:"Environment",
		section_contracts:	"Contracts",
		section_events:		"Events",
		field:				"Field",
		value_a:			"Value A",
		value_b:			"Value B",
		missing_ids:		"Compare link incomplete (a / b parameter missing).",
	},

	card_detail:
	{
		back_to_list:				"← Back to the list",
		read_on:					"{{type}} — {{model}} — read on {{date}}",
		downloads_title:			"Downloads",
		downloads_derived:			"(derived)",
		downloads_error_status:		"Error {{status}}: {{message}}",
		downloads_error_network:	"Network error during download.",
		parts_bundle_title:			"Bundle parts",
		parts_col_name:				"Name",
		parts_col_type:				"Type",
		parts_col_file:				"File",
		parts_col_received:			"Received on",
		raw_metadata_title:			"Raw metadata",
		share_title:				"Share",
		share_email_placeholder:	"Recipient email address",
		share_message_placeholder:	"Message (optional)",
		share_submit:				"Share",
		share_progress:				"Sharing…",
		shares_col_recipient:		"Recipient",
		shares_col_shared_on:		"Shared on",
		shares_col_viewed:			"Viewed",
		shares_col_message:			"Message",
		no_active_shares:			"No active shares for this card.",
		revoke_button:				"Revoke",
		revoke_progress:			"Revoking…",
		revoke_dialog_title:		"Revoke share?",
		revoke_dialog_body:			"The recipient will immediately lose access to this card.",
		revoke_error:				"Error during revocation.",
		share_err_missing_field:	"Required fields missing or invalid email.",
		share_err_recipient:		"No user matches that email.",
		share_err_self:				"You can't share with yourself.",
		share_err_forbidden:		"Access denied.",
		share_err_default:			"Error ({{status}}).",
	},

	profile:
	{
		title:				"My account",
		field_email:		"Email",
		field_role:			"Role",
		field_created_on:	"Account created on",
		field_last_login:	"Last sign-in",
		field_last_lockout:	"Last automatic lockout",
		lockout_warning:	"If this lockout wasn't from you, contact the administrator.",
		email_form_title:	"Change my email",
		new_email_label:	"New address",
		email_same_as_current:	"The new address is identical to the current one.",
		email_updated:		"Email address updated.",
		err_email_taken:	"This address is already used by another account.",
		err_missing_field:	"Required fields missing or invalid.",
		err_unauthenticated:	"Session expired. Please sign in again.",
		err_default:		"Error ({{status}}).",
	},

	shared:
	{
		title_count:			"Shared with me ({{total}})",
		no_shares:				"Nobody has shared a card with you yet.",
		col_serial:				"Serial number",
		col_shared_by:			"Shared by",
		col_received_on:		"Received on",
		col_type:				"Type",
		col_model:				"Model",
		col_message:			"Message",
		new_badge:				"New",
	},

	admin_users:
	{
		title_count:				"Users ({{total}})",
		search_placeholder:			"Search by email…",
		col_email:					"Email",
		col_role:					"Role",
		col_cards:					"Cards",
		col_created:				"Created",
		col_status:					"Status",
		you:						"(you)",
		status_active:				"active",
		status_disabled:			"disabled",
		status_locked:				"locked",
		locked_until_tooltip:		"Locked until {{date}} {{time}}",
		btn_promote:				"→ Admin",
		btn_demote:					"→ User",
		btn_disable:				"Disable",
		btn_reactivate:				"Re-enable",
		btn_unlock:					"Unlock",
		btn_delete:					"Delete",
		btn_resend_welcome:			"Resend invitation",
		welcome_resent_ok:			"Invitation email resent to {{email}}.",
		welcome_resent_err:			"Failed to send invitation email to {{email}}.",
		delete_dialog_title:		"Delete user?",
		delete_dialog_body:			"All the user's cards will also be deleted. This action is irreversible.",
		create_title:				"Create a user",
		create_email:				"Email",
		create_role:				"Role",
		create_submit:				"Create",
		create_progress:			"Creating…",
	},

	audit:
	{
		title_count:			"Audit log ({{total}})",
		filter_action_all:		"All actions",
		filter_actor:			"Actor (email)…",
		filter_date_min:		"Min date",
		filter_date_max:		"Max date",
		no_entries:				"No entries.",
		no_entries_filtered:	"No entries match the filters.",
		col_when:				"When",
		col_action:				"Action",
		col_actor:				"Actor",
		col_ip:					"IP",
		col_target:				"Target",
		col_details:			"Details",
		actor_now_suffix:		"(now {{email}})",
		actor_deleted_suffix:	"(deleted)",
		actor_deleted_tooltip:	"Account deleted",
	},

} as const;
